import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminUserService, type UpdateUserRequest } from '@/services/admin-user.service'
import { format } from 'date-fns'
import { excelExportService } from '@/services/excel-export.service'
import type { ExportUsersRequest, UserExportRow, UserFilters } from '@/types/admin'
import { awaitRefresh } from '@/lib/awaitRefresh'

/**
 * Query keys for user-related queries
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  tenants: (id: string) => [...userKeys.detail(id), 'tenants'] as const,
  auditLogs: (id: string, page: number) => [...userKeys.detail(id), 'audit-logs', page] as const,
}

/**
 * Hook to fetch paginated user list with filters
 * Returns: { data: User[]; pagination: PaginationInfo }
 */
export function useUsers(filters: UserFilters) {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: ({ signal }) => adminUserService.getUsers(filters, signal),
    placeholderData: (previousData) => previousData,
    staleTime: 30000, // 30 seconds
  })
}

/**
 * Hook to fetch user details
 * Returns: UserDetail
 */
export function useUserDetail(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => adminUserService.getUserById(id),
    enabled: !!id,
    staleTime: 60000, // 1 minute
  })
}

/**
 * Hook to fetch user's tenant memberships
 * Returns: TenantMembership[]
 */
export function useUserTenants(id: string) {
  return useQuery({
    queryKey: userKeys.tenants(id),
    queryFn: () => adminUserService.getUserTenants(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch user's audit logs
 * Returns: { data: ActivityLog[]; pagination: PaginationInfo }
 */
export function useUserAuditLogs(
  id: string,
  page: number = 1,
  limit: number = 20,
  actionType?: string,
  dateFrom?: string,
  dateTo?: string
) {
  return useQuery({
    queryKey: [...userKeys.auditLogs(id, page), actionType, dateFrom, dateTo],
    queryFn: () =>
      adminUserService.getUserAuditLogs(id, page, limit, actionType, dateFrom, dateTo),
    enabled: !!id,
  })
}

/*
 * Loading standard for the writes below: invalidation lives in `onSettled` and its promise is
 * RETURNED, so the mutation stays `isPending` until the refetch has landed. The caller's spinner,
 * its held-open dialog and the page overlay therefore lift together with the new state on screen,
 * not on the bare API answer — the header used to keep offering "Suspend" for a user who already
 * was. It runs on failure too, since a request that timed out may still have been applied.
 */

/**
 * Hook to update user
 * Service returns: User (extracted from StandardResponse<User>)
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      adminUserService.updateUser(id, data),
    onSuccess: () => {
      toast.success('User updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update user')
    },
    // Re-read rather than `setQueryData` with the response: the update answers with a `User`, while
    // the detail cache holds a `UserDetail`, and writing one over the other emptied the memberships
    // and activity tabs until the next refetch.
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: userKeys.detail(id) }),
      ])),
  })
}

/**
 * Hook to suspend user
 * Service returns: void (MessageResponse is handled in service)
 */
export function useSuspendUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      adminUserService.suspendUser(id, reason),
    onSuccess: () => {
      toast.success('User suspended successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to suspend user')
    },
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: userKeys.detail(id) }),
      ])),
  })
}

/**
 * Hook to activate user
 * Service returns: void (MessageResponse is handled in service)
 */
export function useActivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      adminUserService.activateUser(id, notes),
    onSuccess: () => {
      toast.success('User activated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to activate user')
    },
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: userKeys.detail(id) }),
      ])),
  })
}

/**
 * Hook to reset user password
 * Service returns: void (MessageResponse is handled in service)
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: (id: string) => adminUserService.resetPassword(id),
    onSuccess: () => {
      toast.success('Password reset email sent successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send password reset email')
    },
  })
}

/**
 * Hook to impersonate user
 * Service returns: ImpersonateResponse (extracted from StandardResponse)
 */
export function useImpersonateUser() {
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminUserService.impersonateUser(id, reason),
    onSuccess: (response) => {
      toast.success('Impersonation started. Redirecting...')
      // Store impersonation token and redirect
      localStorage.setItem('impersonation_token', response.token)
      window.location.href = response.redirectUrl
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to impersonate user')
    },
  })
}

/**
 * Hook to export users to Excel
 *
 * The API returns rows; the workbook is built in the browser by excelExportService, so the
 * backend never has to know about spreadsheet formats.
 */
export function useExportUsers() {
  return useMutation({
    mutationFn: async (payload: ExportUsersRequest) => {
      const rows = await adminUserService.getUsersForExport(payload)

      if (rows.length > 0) {
        await excelExportService.download<UserExportRow>({
          fileName: `users_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`,
          sheetName: 'Users',
          columns: [
            { header: 'Full Name', value: (row) => row.fullName, width: 32 },
            { header: 'Phone Number', value: (row) => row.phoneNumber, width: 18 },
            { header: 'Email', value: (row) => row.email, width: 36 },
          ],
          rows,
        })
      }

      return rows.length
    },
    onSuccess: (rowCount) => {
      // A filter that matches nothing produces no file at all, so saying "exported" would be a
      // lie — tell the operator why nothing was downloaded instead.
      toast.success(
        rowCount === 0
          ? 'No users matched — nothing to export'
          : `Exported ${rowCount} user${rowCount === 1 ? '' : 's'}`
      )
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to export users')
    },
  })
}
